<script setup>
import {
    computed,
    ref,
    defineComponent,
    onBeforeUnmount,
    h,
    getCurrentInstance,
    onMounted,
    onUnmounted,
} from 'vue'
import {
    useFloating,
    autoUpdate,
    shift,
    limitShift,
    offset,
    arrow,
    size,
} from '@floating-ui/vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'

const { proxy } = getCurrentInstance()

onMounted(() => {
    proxy.$bus.on('notification-clicked', onNotificationClicked)
})

onUnmounted(() => {
    proxy.$bus.off('notification-clicked', onNotificationClicked)
})

const onNotificationClicked = () => {
    if (props.componentId == 'notifications') {
        reference.value.click()
    }
}

const props = defineProps({
    offset: {
        type: [Number],
        default: 0,
    },
    componentId: {
        type: [Number, String],
        required: true,
    },
})

const placement = 'bottom'
const availableContentHeight = ref(0)
const reference = ref(null)
const floating = ref(null)
const floatingArrow = ref(null)
const { floatingStyles, middlewareData } = useFloating(reference, floating, {
    placement: placement,
    middleware: [
        shift({
            crossAxis: false,
            limiter: limitShift({ offset: 36 }), // 36 = 20 (arrow offset) + 16 (height of arrow)
            padding: 20,
        }),
        size({
            apply({ availableWidth, availableHeight, elements }) {
                availableContentHeight.value = availableHeight
            },
            padding: 20,
        }),
        offset({ mainAxis: props.offset }),
        arrow({ element: floatingArrow, padding: 20 }),
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
})
const arrowX = computed(() =>
    middlewareData.value.arrow?.x ? `${middlewareData.value.arrow?.x}px` : null
)

const TempComponent = defineComponent({
    emits: ['hidden', 'visible'],
    setup(_, { emit }) {
        onBeforeUnmount(() => {
            emit('hidden')
        })
        onMounted(() => {
            emit('visible')
        })
    },
    render: () => h('div'),
})
</script>

<template>
    <div>
        <Popover v-slot="{ open, close }" class="w-3/4">
            <PopoverButton class="w-fit focus-style">
                <div ref="reference">
                    <slot name="reference" />
                </div>
            </PopoverButton>

            <Teleport to="body">
                <div
                    ref="floating"
                    class="relative z-modal"
                    :style="floatingStyles"
                >
                    <transition
                        enter-active-class="transition-opacity duration-200 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition-opacity duration-150 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <PopoverPanel>
                            <div>
                                <!-- <div
                            ref="floatingArrow"
                            :style="{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                            }"
                            class="hidden md:block before:absolute before:left-0 before:-translate-x-full before:top-0 before:-translate-y-1/2 before:border-8 before:border-transparent before:border-r-white bg-black"
                        /> -->

                                <span
                                    ref="floatingArrow"
                                    :style="{ left: arrowX }"
                                    :class="[
                                        placement === 'top'
                                            ? '-bottom-[4.5px] border-b border-r'
                                            : '-top-[4.5px] border-t border-l',
                                        'absolute rotate-45 aspect-square w-2 -ml-1 z-1 bg-white',
                                    ]"
                                />

                                <div
                                    class="rounded-lg shadow-lg ring-1 ring-black/20 overflow-hidden flex z-0"
                                    :style="{
                                        'max-height': `${availableContentHeight}px`,
                                    }"
                                >
                                    <TempComponent
                                        @hidden="$emit('close')"
                                        @visible="$emit('open')"
                                    />
                                    <slot name="floating" />
                                </div>
                            </div>
                        </PopoverPanel>
                    </transition>
                </div>
            </Teleport>
        </Popover>
    </div>
</template>
